'use client';

import type { FC, ReactNode } from 'react';
import { useCallback, useContext } from 'react';
import { CarouselContext } from './CarouselContext';

/**
 * Scrolls the carousel to a specified index with customizable position and behavior.
 *
 * @param idx - The target index. Out of bounds values are wrapped around.
 * @param position - The target item position: `'start'`, `'center'`, or `'end'`.
 * @param scrollBehavior - The scroll behavior: `'auto'`, `'smooth'`, or `'instant'`.
 */
export type ScrollToIndex = (idx: number, position: 'start' | 'center' | 'end', scrollBehavior?: 'auto' | 'smooth' | 'instant') => void;
export type CarouselInterfaceContext = {
  /**
   * Indicates if the carousel is currently
   */
  enabled: boolean;

  /**
   * An array of indexes of the currently fully visible items.
   */
  visibleIndexes: number[];

  /**
   * The number of currently fully visible items.
   */
  visibleItems: number;

  /**
   * The index of the last item.
   * If you want to display the total number of items, you can use this value by adding 1.
   */
  lastIndex: number;

  /**
   * The number of pixels the stage is scrolled.
   */
  stageScrollLeft: number;

  /**
   * The amount of pixels the stage would require to be rendered without a scrollbar -- its "width".
   */
  stageScrollWidth: number;

  /**
   * The current width of the stage -- its "width" as visible to the user.
   */
  stageWidth: number;
  scrollToIndex: ScrollToIndex;

  /**
   * A function that scrolls the carousel to the next item. The carousel loops to the first item, when invoked at the end of the carousel.
   */
  next: () => void;

  /**
   * A function that scrolls the carousel to the previous item. The carousel loops to the last item, when invoked at the start of the carousel.
   */
  prev: () => void;
};
type Props = {
  children: (context: CarouselInterfaceContext) => ReactNode;
};

/**
 * `CarouselInterface` acts as a comprehensive control and indicator system for the carousel, allowing for enhanced interaction and information display regarding the carousel's state.
 *
 * Utilize this component for various functionalities, including:
 *
 * - Showcasing the total number of items along with the position of the current item, for example, `Picture 1 of 5`.
 * - Implementing navigational controls, such as previous and next buttons, for seamless navigation through carousel items.
 * - Introducing visual indicators or "bullets" that represent the quantity of items and indicate the currently visible item.
 * - Integrating sophisticated control logic using the `scrollToIndex` function, enabling direct navigation to any item within the carousel.
 *
 * > ⚠️ It's imperative to use this component as a direct descendant of the `Carousel` component to ensure proper functionality.
 *
 * Below is a practical example of how to integrate the `CarouselInterface` within the `Carousel` component:
 *
 * ```tsx
 * <UniversalCarousel>
 *   <CarouselItems>
 *     ...
 *   </CarouselItems>
 *   <CarouselInterface>
 *     {(carouselContext) => {
 *       // Extracting properties from the carousel context
 *       const {visibleIndexes, lastIndex, prev, next} = carouselContext;
 *
 *       // Constructing the carousel navigation interface
 *       return (
 *         <div>
 *           <button onClick={prev}>Previous</button>
 *
 *           Picture {visibleIndexes[0] + 1} of {lastIndex + 1}
 *
 *           <button onClick={next}>Next</button>
 *         </div>
 *       );
 *     }}
 *   </CarouselInterface>
 * </UniversalCarousel>
 * ```
 *
 * This code snippet illustrates a simple setup where users can navigate through items and view the current item's context within the carousel. Adjust the logic within `CarouselInterface` to suit specific use cases or UI requirements.
 */
export const CarouselInterface: FC<Props> = ({
  children
}) => {
  const {
    enabled,
    visibleIndexes,
    visibleItems,
    lastIndex,
    scrollToIndex,
    stageScrollLeft,
    stageScrollWidth,
    stageWidth
  } = useContext(CarouselContext);
  const prev = useCallback(() => {
    scrollToIndex(Math.min(...visibleIndexes) - 1, 'start');
  }, [visibleIndexes, scrollToIndex]);
  const next = useCallback(() => {
    if (visibleIndexes.indexOf(lastIndex) > -1) {
      scrollToIndex(0, 'start');
      return;
    }
    scrollToIndex(Math.min(...visibleIndexes) + 1, 'start');
  }, [visibleIndexes, lastIndex, scrollToIndex]);
  return children({
    enabled,
    visibleIndexes,
    visibleItems,
    lastIndex,
    scrollToIndex,
    stageScrollLeft,
    stageScrollWidth,
    stageWidth,
    prev,
    next
  });
};